/**
 * assemblerMapper.ts
 *
 * Adapter: converts the legacy Redux iAssemblyModel (nested sustamizeTargets /
 * customTargets) into the CQS CalculationProcessChainRequest shape before each
 * POST /assembly/calculate.
 *
 * If a step already has flat `targets[]` (new create / migrated models), it is
 * passed through unchanged.
 */

import {
  iAssemblyModel,
  CalculationProcessStep,
  Co2ReferenceObject,
  CalculationProcessChainRequest,
  CalculationProcessStepRequest,
  ProcessStepTargetRequest,
  SustamizeProcessStepTargetRequest,
  CustomProcessStepTargetRequest,
} from 'store/types/assembler';

interface ResolvedRef {
  co2Ref: Co2ReferenceObject;
  contextName: string;
  source: 'sustamize' | 'custom';
}

function collectRefs(
  targets:
    | CalculationProcessStep['sustamizeTargets']
    | CalculationProcessStep['customTargets'],
  source: 'sustamize' | 'custom'
): ResolvedRef[] {
  if (!targets) return [];

  const refs: ResolvedRef[] = [];

  const mat = targets.materials;
  (mat?.metals      || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.metals',     source }));
  (mat?.polymers    || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.polymers',   source }));
  (mat?.composites  || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.composites', source }));
  (mat?.sinters     || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.sinters',    source }));
  (mat?.elements    || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.elements',   source }));
  (mat?.other       || []).forEach(r => refs.push({ co2Ref: r, contextName: '.materials.other',      source }));

  const comp = targets.components;
  (comp?.electronics || []).forEach(r => refs.push({ co2Ref: r, contextName: '.electronics', source }));

  const log = targets.logistics;
  (log?.transportation || []).forEach(r => refs.push({ co2Ref: r, contextName: '.logistics.transportation', source }));
  (log?.packaging      || []).forEach(r => refs.push({ co2Ref: r, contextName: '.logistics.packaging',      source }));

  const prod = targets.production;
  (prod?.manufacturing || []).forEach(r => refs.push({ co2Ref: r, contextName: '.production.manufacturing', source }));
  (prod?.energy     as Co2ReferenceObject[] || []).forEach(r => refs.push({ co2Ref: r, contextName: '.production.energy',     source }));
  (prod?.employees  as Co2ReferenceObject[] || []).forEach(r => refs.push({ co2Ref: r, contextName: '.production.employees',  source }));
  (prod?.equipment  as Co2ReferenceObject[] || []).forEach(r => refs.push({ co2Ref: r, contextName: '.production.equipment',  source }));

  return refs;
}

function buildSustamizeTarget(ref: ResolvedRef): SustamizeProcessStepTargetRequest {
  const { co2Ref, contextName } = ref;
  const params = (co2Ref.params ?? {}) as Record<string, unknown>;

  return {
    referenceType: 'sustamize',
    customName:    co2Ref.name,
    comment:       co2Ref.comment ?? undefined,
    source:        typeof co2Ref.source === 'string' && co2Ref.source !== 'sustamize'
      ? co2Ref.source
      : (co2Ref.documentationLink ?? undefined),
    contextName,

    selectionParameters: {
      sid:         co2Ref.sid ?? '',
      countryCode: (params['countryCode'] as string | null | undefined) ?? undefined,
      shape:       (params['shape']       as string | null | undefined) ?? undefined,
    },

    calculationParameters: { ...params },
  };
}

function buildCustomTarget(ref: ResolvedRef): CustomProcessStepTargetRequest {
  const { co2Ref, contextName } = ref;
  const params = (co2Ref.params ?? {}) as Record<string, unknown>;

  return {
    referenceType: 'custom',
    customName:    co2Ref.name,
    comment:       co2Ref.comment ?? undefined,
    source:        co2Ref.source !== 'custom' ? co2Ref.source : undefined,
    contextName,

    calculationParameters: {
      customFootprint:
        (params['customFootprint'] as number | null | undefined) ??
        co2Ref.calculatedEntityEmission ??
        0,
      customOriginComposition:
        (params['customOriginComposition'] as object | null | undefined) ??
        co2Ref.calculatedEntityEmissionOriginComposition ??
        undefined,
      unit: co2Ref.unit ?? 'kgCO2e/pcs',
      ...params,
    },
  };
}

function mapStep(
  step: CalculationProcessStep & { targets?: ProcessStepTargetRequest[] },
  fallbackPosition: number
): CalculationProcessStepRequest {
  const position = step.position ?? fallbackPosition;

  // Prefer non-empty flat targets (migrated / already-CQS steps).
  if (Array.isArray(step.targets) && step.targets.length > 0) {
    return {
      name: step.name,
      position,
      comment: step.comment ?? undefined,
      targets: step.targets,
    };
  }

  // Fall back to legacy nested buckets (Assembler UI still writes here).
  const sustamizeRefs = collectRefs(step.sustamizeTargets, 'sustamize');
  const customRefs    = collectRefs(step.customTargets ?? undefined, 'custom');

  const targets: ProcessStepTargetRequest[] = [
    ...sustamizeRefs.map(buildSustamizeTarget),
    ...customRefs.map(buildCustomTarget),
  ];

  // Empty step (no nested content and no flat targets) is allowed.
  return {
    name: step.name,
    position,
    comment: step.comment ?? undefined,
    targets,
  };
}

export function mapReduxStateToCalculationRequest(
  reduxState: iAssemblyModel
): CalculationProcessChainRequest {
  const steps = reduxState?.assemblyModel?.calculationProcessChain || [];

  const mappedSteps: CalculationProcessStepRequest[] = steps.map((step, index) =>
    mapStep(step as CalculationProcessStep & { targets?: ProcessStepTargetRequest[] }, index + 1)
  );

  return {
    name: reduxState?.name ?? undefined,
    calculationProcessChain: mappedSteps,
  };
}

export function isValidCalculationRequest(
  req: CalculationProcessChainRequest
): boolean {
  return req.calculationProcessChain.length > 0;
}
